import React, { useState, useEffect } from 'react'
import { MessageCircle } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { UI, inputClass } from '../utils/styles'
import { ButtonPrimary } from '../components/ui/Button'
import { ChevronDown, Trash, Download, Building, Users, Calendar, HelpCircle, FileText, LinkIcon, Edit } from '../components/ui/Icons'

export function SettingsPage() {
  const {
    session,
    operation,
    meters,
    isOwner,
    expandedSettingsSection,
    setExpandedSettingsSection,
    editingFirstName,
    setEditingFirstName,
    newFirstName,
    setNewFirstName,
    editingOpName,
    setEditingOpName,
    newOpName,
    setNewOpName,
    seasonStart,
    setSeasonStart,
    seasonEnd,
    setSeasonEnd,
    teamMembers,
    inviteCode,
    showExportModal,
    setShowExportModal,
    handleUpdateFirstName,
    handleUpdateOpName,
    handleUpdateSeasonDates,
    handleExportCSV,
    handleExportPDF,
    confirmSeasonStartFromPreviousSeason,
    backfillSeasonStartReadings,
    shareInviteLink,
    removeTeamMember,
    doLogout,
    showToast,
  } = useApp()

  // Support message state
  const [supportMessage, setSupportMessage] = useState('')
  const [sendingSupport, setSendingSupport] = useState(false)

  // Season year picker state
  const [showCustomDates, setShowCustomDates] = useState(false)

  // Auto-show custom dates if stored dates aren't a full year (Jan 1 – Dec 31)
  useEffect(() => {
    if (operation?.season_start && operation?.season_end) {
      const s = operation.season_start
      const e = operation.season_end
      const isFullYr = s.endsWith('-01-01') && e.endsWith('-12-31') && s.substring(0, 4) === e.substring(0, 4)
      if (!isFullYr) setShowCustomDates(true)
    }
  }, [operation?.season_start, operation?.season_end])

  const seasonYear = operation?.season_start
    ? parseInt(operation.season_start.substring(0, 4))
    : new Date().getFullYear()

  const isFullYear = operation?.season_start && operation?.season_end
    && operation.season_start.endsWith('-01-01')
    && operation.season_end.endsWith('-12-31')
    && operation.season_start.substring(0, 4) === operation.season_end.substring(0, 4)

  const handleYearChange = (delta) => {
    const newYear = seasonYear + delta
    handleUpdateSeasonDates(`${newYear}-01-01`, `${newYear}-12-31`)
    setShowCustomDates(false)
  }

  const formatSeasonDate = (dateStr) => {
    if (!dateStr) return 'Not set'
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const toggleSection = (key) => setExpandedSettingsSection(expandedSettingsSection === key ? null : key)

  const userName = session?.user?.user_metadata?.first_name || ''
  const userEmail = session?.user?.email || ''

  return (
    <div className="px-4 md:px-6 max-w-2xl mx-auto py-4">
      {/* Header */}
      <h2 className="text-lg md:text-xl font-semibold text-slate-800 mb-4 tracking-tight">Settings</h2>

      {/* User Info */}
      <div className="flex justify-between items-start py-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-800">{userName || 'No name set'}</p>
            <button onClick={() => { setEditingFirstName(true); setNewFirstName(userName) }} className="text-gray-400 hover:text-gray-500">
              <Edit />
            </button>
          </div>
          {editingFirstName && (
            <div className="flex gap-2 mt-2">
              <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className={`${inputClass} !py-2`} placeholder="Enter name" />
              <ButtonPrimary onClick={handleUpdateFirstName} className="px-3 !py-2 text-sm">Save</ButtonPrimary>
              <button onClick={() => { setEditingFirstName(false); setNewFirstName('') }} className="px-2 py-1 text-slate-500 hover:text-slate-800 text-sm transition-colors">Cancel</button>
            </div>
          )}
          <p className="text-sm text-slate-500">{userEmail}</p>
        </div>
        <button onClick={doLogout} className="text-sm text-red-500">Sign out</button>
      </div>
      <div className="border-b border-slate-200" />

      {/* Season summary */}
      {operation?.season_start && (
        <p className="text-sm text-slate-500 py-3">
          Season: {isFullYear ? seasonYear : `${formatSeasonDate(operation.season_start)} – ${formatSeasonDate(operation.season_end)}`}
        </p>
      )}

      {/* Settings rows — one flat list */}
      {/* Operation Name */}
      <button onClick={() => toggleSection('opname')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
        <div className="flex items-center gap-3">
          <span className="text-slate-400"><Building /></span>
          <span className="font-medium text-slate-900">Operation Name</span>
        </div>
        <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'opname' ? 'rotate-180' : ''}`} />
      </button>
      {expandedSettingsSection === 'opname' && (
        <div className="pb-4 space-y-3">
          {isOwner && editingOpName ? (
            <div className="space-y-3">
              <input type="text" value={newOpName} onChange={(e) => setNewOpName(e.target.value)} className={inputClass} placeholder="Operation name" />
              <div className="flex gap-2">
                <ButtonPrimary onClick={handleUpdateOpName} className="flex-1">Save</ButtonPrimary>
                <button onClick={() => { setEditingOpName(false); setNewOpName('') }} className="flex-1 px-4 py-2 text-slate-600 hover:text-slate-800">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-900">{operation?.op_name}</p>
              {isOwner && (
                <button onClick={() => { setEditingOpName(true); setNewOpName(operation?.op_name || '') }} className="text-cyan-700 hover:text-cyan-800 text-sm">Edit</button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="border-b border-slate-200" />

      {/* Team Members */}
      {isOwner && (
        <>
          <button onClick={() => toggleSection('team')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
            <div className="flex items-center gap-3">
              <span className="text-slate-400"><Users width="20" height="20" /></span>
              <span className="font-medium text-slate-900">Team Members</span>
            </div>
            <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'team' ? 'rotate-180' : ''}`} />
          </button>
          {expandedSettingsSection === 'team' && (
            <div className="pb-4">
              {inviteCode && (
                <div className="mb-3">
                  <button onClick={shareInviteLink} className="flex items-center gap-3 py-2 text-left active:opacity-70">
                    <span className="text-sky-700"><LinkIcon /></span>
                    <span className="text-sm font-medium text-sky-700 hover:text-sky-800">Share Invite Link</span>
                  </button>
                  <p className="text-sm text-slate-400">Send this invite link to your team by text or email to give them access to your operation.</p>
                </div>
              )}
              {teamMembers.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-500 mb-2">Team Members ({teamMembers.length})</div>
                  {teamMembers.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between py-2.5">
                      <div>
                        <p className="font-medium text-slate-900">{member.email}</p>
                        <p className="text-xs text-slate-400 capitalize">{member.role}</p>
                      </div>
                      {member.role === 'member' && (
                        <button onClick={() => removeTeamMember(member.user_id)} className="text-red-500 hover:text-red-600 text-xs font-semibold px-3 py-1 border border-red-300 rounded-lg">
                          Revoke Access
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="border-b border-slate-200" />
        </>
      )}

      {/* Season */}
      {isOwner && (
        <>
          <button onClick={() => toggleSection('season')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
            <div className="flex items-center gap-3">
              <span className="text-slate-400"><Calendar /></span>
              <span className="font-medium text-slate-900">Season</span>
            </div>
            <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'season' ? 'rotate-180' : ''}`} />
          </button>
          {expandedSettingsSection === 'season' && (
            <div className="pb-4">
              {!showCustomDates ? (
                <>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleYearChange(-1)}
                        className="p-1.5 text-slate-400 active:text-slate-600"
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <span className="text-xl font-semibold text-slate-900 tabular-nums">{seasonYear}</span>
                      <button
                        onClick={() => handleYearChange(1)}
                        className="p-1.5 text-slate-400 active:text-slate-600"
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setShowCustomDates(true)
                        setSeasonStart(operation?.season_start || `${seasonYear}-01-01`)
                        setSeasonEnd(operation?.season_end || `${seasonYear}-12-31`)
                      }}
                      className="text-sm text-slate-400"
                    >
                      Custom dates
                    </button>
                  </div>
                  {meters.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button onClick={confirmSeasonStartFromPreviousSeason} className="block text-sm text-sky-700 hover:text-sky-800">
                        Carry over from {seasonYear - 1}
                      </button>
                      <p className="text-xs text-slate-400 mt-1">Copies each meter's last reading as the new Season Start reading</p>
                    </div>
                  )}
                  {meters.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button onClick={backfillSeasonStartReadings} className="block text-sm text-sky-700 hover:text-sky-800">
                        Backfill season start readings
                      </button>
                      <p className="text-xs text-slate-400 mt-1">Creates reading records for season starts that don't have one yet</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1.5">Start Date</label>
                    <input type="date" value={seasonStart} onChange={(e) => setSeasonStart(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1.5">End Date</label>
                    <input type="date" value={seasonEnd} onChange={(e) => setSeasonEnd(e.target.value)} className={inputClass} />
                  </div>
                  <ButtonPrimary
                    onClick={() => handleUpdateSeasonDates(seasonStart, seasonEnd)}
                    className="w-full"
                  >
                    Save Dates
                  </ButtonPrimary>
                  <button
                    onClick={() => handleYearChange(0)}
                    className="text-sm text-slate-400"
                  >
                    Reset to full year
                  </button>
                  {meters.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button onClick={confirmSeasonStartFromPreviousSeason} className="block text-sm text-sky-700 hover:text-sky-800">
                        Carry over from {seasonYear - 1}
                      </button>
                      <p className="text-xs text-slate-400 mt-1">Copies each meter's last reading as the new Season Start reading</p>
                    </div>
                  )}
                  {meters.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button onClick={backfillSeasonStartReadings} className="block text-sm text-sky-700 hover:text-sky-800">
                        Backfill season start readings
                      </button>
                      <p className="text-xs text-slate-400 mt-1">Creates reading records for season starts that don't have one yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="border-b border-slate-200" />
        </>
      )}

      {/* Export Data (owner only) */}
      {isOwner && (
        <>
          <button onClick={() => toggleSection('export')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
            <div className="flex items-center gap-3">
              <span className="text-slate-400"><Download /></span>
              <span className="font-medium text-slate-900">Export Data</span>
            </div>
            <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'export' ? 'rotate-180' : ''}`} />
          </button>
          {expandedSettingsSection === 'export' && (
            <div>
              <button onClick={() => setShowExportModal(true)} className="w-full py-3 pl-10 flex items-center gap-3 text-left active:bg-slate-50">
                <span className="text-slate-400"><FileText /></span>
                <span className="font-medium text-slate-900">Export PDF</span>
              </button>
              <div className="border-b border-slate-200 ml-10" />
              <button onClick={handleExportCSV} className="w-full py-3 pl-10 flex items-center gap-3 text-left active:bg-slate-50">
                <span className="text-slate-400"><Download /></span>
                <span className="font-medium text-slate-900">Download CSV</span>
              </button>
            </div>
          )}
          <div className="border-b border-slate-200" />
        </>
      )}

      {/* How-To Guide */}
      <button onClick={() => toggleSection('guide')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
        <div className="flex items-center gap-3">
          <span className="text-slate-400"><HelpCircle /></span>
          <span className="font-medium text-slate-900">How-To Guide</span>
        </div>
        <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'guide' ? 'rotate-180' : ''}`} />
      </button>
      {expandedSettingsSection === 'guide' && (
        <div className="pb-4 space-y-4 text-sm text-slate-700">
          <div>
            <h4 className="font-semibold mb-1">Adding a Meter</h4>
            <p>Go to the Meters tab and tap "Add Meter". Enter the meter name, acres it covers, and the units your meter displays. You can also add optional details like brand, serial number, well output GPM, and notes. If you already have a reading on the meter, check "Set season starting reading" to enter it during setup.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Editing & Deleting Meters</h4>
            <p>Tap a meter to expand it, then tap the edit icon to change its name, acres, units, or other details. To delete a meter, open the edit screen and tap "Delete". This will remove the meter and all of its readings permanently.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Recording a Reading</h4>
            <p>Go to Add Reading, select your meter, and enter the current meter reading. The app shows your previous reading for reference and automatically calculates inches applied and acre-inches. You can optionally add notes and snap a photo of the meter register. If the reading is lower than the last one, the app will ask if the meter rolled over.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Calculator Mode</h4>
            <p>Switch to Calculator mode on the Add Reading page for quick, one-off calculations. Enter a previous reading, current reading, acres, and units to see results instantly — nothing is saved.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Starting a New Season</h4>
            <p>In Settings, set your season year or choose custom start and end dates. Then tap "Carry over from [previous year]" to automatically use each meter's last reading as the new season starting point. The app will also prompt you to carry over when a new season is detected.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Operation Name</h4>
            <p>Tap Operation Name in Settings to view or rename your operation. This is the name your team members will see when they join.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Inviting Team Members</h4>
            <p>Go to Settings → Team Members and tap "Share Invite Link". Send the link to your team via text or email. When they tap the link and sign up, they'll automatically be added to your operation.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">What Team Members Can Do</h4>
            <p>Team members can view all meters, add readings, and attach photos — everything needed for day-to-day field work. Operation settings like editing meters, managing the season, and exporting data are reserved for the account owner.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Your Name & Readings</h4>
            <p>Your name shown on readings comes from your profile at the top of Settings. Tap the edit icon next to your name to change it. New readings will use your updated name going forward.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Revoking Team Member Access</h4>
            <p>In Settings → Team Members, tap "Revoke Access" next to any member to remove them from your operation. This also resets your invite link so the old one can no longer be used. You'll get a new invite link to share with anyone you still want on your team.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Exporting Your Data</h4>
            <p>In Settings → Export Data, choose "Export PDF" for a formatted report (with or without meter photos) or "Download CSV" for a spreadsheet-friendly file of all your readings.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Quick Tips</h4>
            <p><span className="font-medium text-gray-500">{'\u2192'} Tip: </span><span className="text-gray-400 font-normal">Tap a meter on the Meters page to see its season total, last irrigation, and full reading history.</span></p>
            <p className="mt-1"><span className="font-medium text-gray-500">{'\u2192'} Tip: </span><span className="text-gray-400 font-normal">If you revoke a team member's access, your invite link resets automatically — share the new one with anyone who still needs access.</span></p>
          </div>
        </div>
      )}
      <div className="border-b border-slate-200" />

      {/* Got a question? */}
      <button onClick={() => toggleSection('support')} className="w-full py-3.5 flex justify-between items-center text-left active:bg-slate-50">
        <div className="flex items-center gap-3">
          <span className="text-slate-400"><MessageCircle size={20} /></span>
          <span className="font-medium text-slate-900">Question or Suggestion?</span>
        </div>
        <ChevronDown className={`text-slate-400 transition-transform ${expandedSettingsSection === 'support' ? 'rotate-180' : ''}`} />
      </button>
      {expandedSettingsSection === 'support' && (
        <div className="pb-4 space-y-3">
          <textarea
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            placeholder="Ask a question or leave a comment..."
            className={`${inputClass} min-h-[120px] resize-none`}
            rows={4}
          />
          <p className="text-xs text-slate-400">We'll respond to {userEmail}</p>
          <ButtonPrimary
            onClick={async () => {
              if (!supportMessage.trim() || sendingSupport) return
              setSendingSupport(true)
              try {
                const res = await fetch('/.netlify/functions/send-support-email', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    message: supportMessage.trim(),
                    operationName: operation?.op_name || '',
                    userName: userName || '',
                    userEmail: userEmail || '',
                  }),
                })
                if (!res.ok) throw new Error('Send failed')
                showToast('Message sent! We\'ll get back to you soon.', 'success')
                setSupportMessage('')
              } catch {
                showToast('Couldn\'t send message. Try again.', 'error')
              } finally {
                setSendingSupport(false)
              }
            }}
            disabled={!supportMessage.trim() || sendingSupport}
            className="w-full"
          >
            {sendingSupport ? 'Sending...' : 'Send'}
          </ButtonPrimary>
        </div>
      )}
    </div>
  )
}
